import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import * as Types from 'types';
import { ImageAlbum, UserStatuses } from 'types';
import * as Thunks from './thunk';

export const imageAlbumAdapter = createEntityAdapter<ImageAlbum>({
  selectId: (image) => image.image_id,
});
export const siteUserStatusAdapter = createEntityAdapter<UserStatuses>({
  selectId: (item) => _.uniqueId(item.user_id),
});

export const approvedAdapter = createEntityAdapter<Types.GetApproved>({
  selectId: (approved) => approved.id,
});

interface InitialState {
  images: EntityState<ImageAlbum>;
  approved: EntityState<Types.GetApproved>;
  loadingApproved: boolean;
  total: number;
  maxPoint: number;
  workerStatus: {
    sites: Types.WorkerStatus[];
    loading: boolean;
  };
  workRisk: {
    sites: Types.WorkRisk[];
    loading: boolean;
  };
  slingTool: {
    sites: Types.CheckSlingTool[];
    loading: boolean;
  };
  listTestScaffold: {
    sites: Types.TestScaffold[];
    loading: boolean;
  };
  safetyInstructions: {
    sites: Types.SafetyInstructions[];
    loading: boolean;
  };
  dataChart: {
    data: Types.WorkerShift[];
    loading: boolean;
  };
  tableForm: {
    data: Types.WorkUser[] | null;
    loading: boolean;
  };
  postInformation: {
    information: Types.InformationPost[] | null;
    loading: boolean;
  };
  deleteImage: {
    images: Types.ImagesId[];
    loading: boolean;
  };
  image: Types.GetDetailImageResponse | null;
  addImage: Types.AddImageResponse | null;
  uploadImage: {
    upload: { success: Types.ImagesId[]; errors: Types.ImagesId[] };
    loading: boolean;
  };
  locationCmAgent: Types.TableCmAgent | null;
  SiteShopUserSchedule: {
    data: Types.GetUserSiteShopListResponse | null;
    loading: boolean;
  };
  SiteCooperatorListToday: {
    data: Types.GetUserSiteShopListTodayResponse | null;
    loading: boolean;
  };
  KYDataApproveBySiteManage: {
    data: Types.GetKYDataApproveBySiteManageResponse | null;
    loading: boolean;
  };
  TodayFootingCheck: {
    data: Types.TodayFootingcheckResponse | null;
    loading: boolean;
  };
  TodaySafetyOrder: {
    data: Types.TodaySafetyOrderResponse | null;
    loading: boolean;
  };
  TodaySlingtool: {
    data: Types.TodaySlingToolResponse | null;
    loading: boolean;
  };
  SiteUserStatus: {
    data: EntityState<Types.UserStatuses>;
    loading: boolean;
  };
  Performance: {
    data: Types.GetPerformanceResponse | null;
    loading: boolean;
  };
  PerformanceWorkArea: {
    data: Types.GetPerformanceWorkAreaResponse[] | null;
    loading: boolean;
  };
  Ky: {
    data: Types.GetKyResponse | null;
    loading: boolean;
  };
  shop: {
    data: Types.Schedule[];
    loading: boolean;
  };
  schedules: {
    data: Types.SchedulesDate[];
    loading: boolean;
  };
  // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
  error_message: string;
  // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
}

export const initialState: InitialState = {
  images: imageAlbumAdapter.getInitialState(),
  approved: approvedAdapter.getInitialState(),
  loadingApproved: false,
  total: 0,
  maxPoint: 0,
  workerStatus: {
    sites: [],
    loading: false,
  },

  workRisk: {
    sites: [],
    loading: false,
  },
  slingTool: {
    sites: [],
    loading: false,
  },
  listTestScaffold: {
    sites: [],
    loading: false,
  },
  safetyInstructions: {
    sites: [],
    loading: false,
  },
  dataChart: {
    data: [],
    loading: false,
  },
  tableForm: {
    data: null,
    loading: false,
  },
  postInformation: {
    information: null,
    loading: false,
  },
  deleteImage: {
    images: [],
    loading: false,
  },
  image: null,
  addImage: null,
  uploadImage: {
    upload: {
      success: [],
      errors: [],
    },
    loading: false,
  },
  locationCmAgent: null,
  KYDataApproveBySiteManage: {
    data: null,
    loading: false,
  },
  TodayFootingCheck: {
    data: null,
    loading: false,
  },
  TodaySafetyOrder: {
    data: null,
    loading: false,
  },
  TodaySlingtool: {
    data: null,
    loading: false,
  },
  SiteUserStatus: {
    data: siteUserStatusAdapter.getInitialState(),
    loading: false,
  },
  SiteShopUserSchedule: {
    data: null,
    loading: false,
  },
  SiteCooperatorListToday: {
    data: null,
    loading: false,
  },
  Performance: {
    data: null,
    loading: false,
  },
  PerformanceWorkArea: {
    data: null,
    loading: true,
  },

  Ky: {
    data: null,
    loading: false,
  },
  shop: {
    data: [],
    loading: false,
  },

  schedules: {
    data: [],
    loading: false,
  },
  // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
  error_message: '',
  // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
};

export const siteDetailSlice = createSlice({
  name: 'siteDetail-page',
  initialState,
  reducers: {
    setDataWorkerStatus(state, { payload }) {
      const index = state.workerStatus.sites.findIndex((obj) => obj.id === payload.id);
      state.workerStatus.sites[index].safety_order_check_status = 1;
    },
    setMaxPoint(state, { payload }) {
      state.maxPoint = payload;
    },
    toggleCheckImg(state, action: PayloadAction<{ id: number; isSelected: boolean }>) {
      imageAlbumAdapter.updateOne(state.images, {
        id: action.payload.id,
        changes: { isSelected: action.payload.isSelected },
      });
    },
    updateManyImg(
      state,
      action: PayloadAction<{ data: { id: number; changes: { isSelected: boolean } }[] }>
    ) {
      imageAlbumAdapter.updateMany(state.images, action.payload.data);
    },
    removeImg(state, action: PayloadAction<{ ids: number[] }>) {
      imageAlbumAdapter.removeMany(state.images, action.payload.ids);
    },
    addImage(state, { payload }) {
      imageAlbumAdapter.setAll(state.images, payload);
    },
    removeApproved(state, action: PayloadAction<{ ids: number[] }>) {
      approvedAdapter.removeMany(state.approved, action.payload.ids);
    },
    setLocationCmAgent(state, { payload }) {
      if (payload) {
        state.locationCmAgent = payload.state;
      }
    },
    updateStatusSkyCheck(
      state,
      action: PayloadAction<{
        id: number | string;
        changes: { ky_check: number; safety_order_check_status: number; rky_check_flag: number };
      }>
    ) {
      siteUserStatusAdapter.updateOne(state.SiteUserStatus.data, action.payload);
    },
    setSiteUserStatus(state, { payload }) {
      siteUserStatusAdapter.setAll(state.SiteUserStatus.data, payload.user_statuses);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(Thunks.getAlbumsImage.fulfilled, (state, action) => {
      imageAlbumAdapter.setAll(state.images, action.payload.images);
      state.total = action.payload.total;
    });
    builder.addCase(Thunks.getAlbumsImage.rejected, (state) => {
      state.images = initialState.images;
      state.total = initialState.total;
    });

    // getSiteUserStatus
    builder.addCase(Thunks.getSiteUserStatus.pending, (state) => {
      state.SiteUserStatus.loading = true;
      siteUserStatusAdapter.setAll(state.SiteUserStatus.data, []);
    });
    builder.addCase(Thunks.getSiteUserStatus.fulfilled, (state, { payload }) => {
      siteUserStatusAdapter.setAll(state.SiteUserStatus.data, payload.user_statuses);
      state.SiteUserStatus.loading = false;
    });
    builder.addCase(Thunks.getSiteUserStatus.rejected, (state) => {
      state.SiteUserStatus.data = initialState.SiteUserStatus.data;
      state.SiteUserStatus.loading = false;
    });

    // 2025/02/27 FJ 3月分反映分 NO32 start
    // getSiteUserGroupStatus
    builder.addCase(Thunks.getSiteUserGroupStatus.pending, (state) => {
      state.SiteUserStatus.loading = true;
      siteUserStatusAdapter.setAll(state.SiteUserStatus.data, []);
    });
    builder.addCase(Thunks.getSiteUserGroupStatus.fulfilled, (state, { payload }) => {
      siteUserStatusAdapter.setAll(state.SiteUserStatus.data, payload.user_statuses);
      state.SiteUserStatus.loading = false;
    });
    builder.addCase(Thunks.getSiteUserGroupStatus.rejected, (state) => {
      state.SiteUserStatus.data = initialState.SiteUserStatus.data;
      state.SiteUserStatus.loading = false;
    });
    // 2025/02/27 FJ 3月分反映分 NO32 end

    builder.addCase(Thunks.getWorkerStatus.pending, (state) => {
      state.workerStatus.loading = true;
    });
    builder.addCase(Thunks.getWorkerStatus.rejected, (state) => {
      state.workerStatus.sites = initialState.workerStatus.sites;
      state.workerStatus.loading = false;
    });
    builder.addCase(Thunks.getWorkerStatus.fulfilled, (state, { payload }) => {
      state.workerStatus = { sites: payload.sites, loading: false };
    });

    builder.addCase(Thunks.getCheckSlingTool.pending, (state) => {
      state.slingTool.loading = true;
    });
    builder.addCase(Thunks.getCheckSlingTool.rejected, (state) => {
      state.slingTool.sites = initialState.slingTool.sites;
      state.slingTool.loading = false;
    });
    builder.addCase(Thunks.getCheckSlingTool.fulfilled, (state, { payload }) => {
      state.slingTool = { sites: payload.sites, loading: false };
    });

    builder.addCase(Thunks.getListTestScaffold.pending, (state) => {
      state.listTestScaffold.loading = true;
    });
    builder.addCase(Thunks.getListTestScaffold.rejected, (state) => {
      state.listTestScaffold.sites = initialState.listTestScaffold.sites;
      state.listTestScaffold.loading = false;
    });
    builder.addCase(Thunks.getListTestScaffold.fulfilled, (state, { payload }) => {
      state.listTestScaffold = { sites: payload.sites, loading: false };
    });

    builder.addCase(Thunks.getSafetyInstructions.pending, (state) => {
      state.safetyInstructions.loading = true;
    });
    builder.addCase(Thunks.getSafetyInstructions.fulfilled, (state, { payload }) => {
      state.safetyInstructions = { sites: payload.sites, loading: false };
    });
    builder.addCase(Thunks.getSafetyInstructions.rejected, (state) => {
      state.safetyInstructions.sites = initialState.safetyInstructions.sites;
      state.safetyInstructions.loading = false;
    });

    builder.addCase(Thunks.getWorkerShift.pending, (state) => {
      state.dataChart.loading = true;
    });
    builder.addCase(Thunks.getWorkerShift.fulfilled, (state, { payload }) => {
      state.dataChart.data = payload.data;
      state.dataChart.loading = false;
    });
    builder.addCase(Thunks.getWorkerShift.rejected, (state) => {
      state.dataChart.data = initialState.dataChart.data;
      state.dataChart.loading = false;
    });

    builder.addCase(Thunks.postInformation.pending, (state) => {
      state.postInformation.loading = true;
    });
    builder.addCase(Thunks.postInformation.fulfilled, (state, { payload }) => {
      state.postInformation.information = payload.information;
      state.postInformation.loading = false;
    });
    builder.addCase(Thunks.postInformation.rejected, (state) => {
      state.postInformation.loading = false;
    });

    builder.addCase(Thunks.deleteImage.pending, (state) => {
      state.deleteImage.loading = true;
    });
    builder.addCase(Thunks.deleteImage.fulfilled, (state, { payload }) => {
      state.deleteImage.images = payload.images;
      state.deleteImage.loading = false;
    });
    builder.addCase(Thunks.deleteImage.rejected, (state) => {
      state.deleteImage.loading = false;
    });

    builder.addCase(Thunks.getDetailImage.fulfilled, (state, { payload }) => {
      state.image = payload;
    });

    builder.addCase(Thunks.addImages.fulfilled, (state, { payload }) => {
      state.addImage = payload;
    });

    builder.addCase(Thunks.uploadImages.pending, (state) => {
      state.uploadImage.loading = true;
    });
    builder.addCase(Thunks.uploadImages.fulfilled, (state, { payload }) => {
      state.uploadImage.upload = payload;
      state.uploadImage.loading = false;
    });
    builder.addCase(Thunks.uploadImages.rejected, (state) => {
      state.uploadImage.loading = false;
    });

    builder.addCase(Thunks.getStatusApproved.pending, (state, { payload }) => {
      state.loadingApproved = true;
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
      state.error_message = '';
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
    });
    builder.addCase(Thunks.getStatusApproved.fulfilled, (state, { payload }) => {
      approvedAdapter.setAll(state.approved, payload.sites);
      state.loadingApproved = false;
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
      state.error_message = '';
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
    });
    builder.addCase(Thunks.getStatusApproved.rejected, (state, payload: any) => {
      state.loadingApproved = false;
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
      state.error_message = payload?.payload?.response?.data?.message || payload.error.message;
      // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
    });
    // SiteShopUserSchedule
    builder.addCase(Thunks.getSiteShopUserSchedule.pending, (state) => {
      state.SiteShopUserSchedule.loading = true;
      state.SiteShopUserSchedule.data = null;
    });
    builder.addCase(Thunks.getSiteShopUserSchedule.fulfilled, (state, { payload }) => {
      state.SiteShopUserSchedule.loading = false;
      state.SiteShopUserSchedule.data = payload;
    });
    builder.addCase(Thunks.getSiteShopUserSchedule.rejected, (state) => {
      state.SiteShopUserSchedule.loading = false;
      state.SiteShopUserSchedule.data = null;
    });

    // SiteShopUserScheduleToday
    builder.addCase(Thunks.getSiteCooperatorListToday.pending, (state) => {
      state.SiteCooperatorListToday.loading = true;
      state.SiteCooperatorListToday.data = null;
    });
    builder.addCase(Thunks.getSiteCooperatorListToday.fulfilled, (state, { payload }) => {
      state.SiteCooperatorListToday.loading = false;
      state.SiteCooperatorListToday.data = payload;
    });
    builder.addCase(Thunks.getSiteCooperatorListToday.rejected, (state) => {
      state.SiteCooperatorListToday.loading = false;
      state.SiteCooperatorListToday.data = null;
    });

    // 2025/02/25 FJ 3月分反映分 NO32 start
    // getSiteFirstCooperatorListToday
    builder.addCase(Thunks.getSiteFirstCooperatorListToday.pending, (state) => {
      state.SiteCooperatorListToday.loading = true;
      state.SiteCooperatorListToday.data = null;
    });
    builder.addCase(Thunks.getSiteFirstCooperatorListToday.fulfilled, (state, { payload }) => {
      state.SiteCooperatorListToday.loading = false;
      state.SiteCooperatorListToday.data = payload;
    });
    builder.addCase(Thunks.getSiteFirstCooperatorListToday.rejected, (state) => {
      state.SiteCooperatorListToday.loading = false;
      state.SiteCooperatorListToday.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 end

    // getTodaySlingtool
    builder.addCase(Thunks.getTodaySlingtool.pending, (state) => {
      state.TodaySlingtool.loading = true;
      state.TodaySlingtool.data = null;
    });
    builder.addCase(Thunks.getTodaySlingtool.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodaySlingtool.data = payload;
    });
    builder.addCase(Thunks.getTodaySlingtool.rejected, (state) => {
      state.TodaySlingtool.loading = false;
      state.TodaySlingtool.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 start
    // getTodaySlingtoolGroup
    builder.addCase(Thunks.getTodaySlingtoolGroup.pending, (state) => {
      state.TodaySlingtool.loading = true;
      state.TodaySlingtool.data = null;
    });
    builder.addCase(Thunks.getTodaySlingtoolGroup.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodaySlingtool.data = payload;
    });
    builder.addCase(Thunks.getTodaySlingtoolGroup.rejected, (state) => {
      state.TodaySlingtool.loading = false;
      state.TodaySlingtool.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 end
    // TodayFootingCheck
    builder.addCase(Thunks.getTodayFootingCheck.pending, (state) => {
      state.TodayFootingCheck.loading = true;
      state.TodayFootingCheck.data = null;
    });
    builder.addCase(Thunks.getTodayFootingCheck.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodayFootingCheck.data = payload;
    });
    builder.addCase(Thunks.getTodayFootingCheck.rejected, (state) => {
      state.TodayFootingCheck.loading = false;
      state.TodayFootingCheck.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 start
    // getTodayFootingCheckGroup
    builder.addCase(Thunks.getTodayFootingCheckGroup.pending, (state) => {
      state.TodayFootingCheck.loading = true;
      state.TodayFootingCheck.data = null;
    });
    builder.addCase(Thunks.getTodayFootingCheckGroup.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodayFootingCheck.data = payload;
    });
    builder.addCase(Thunks.getTodayFootingCheckGroup.rejected, (state) => {
      state.TodayFootingCheck.loading = false;
      state.TodayFootingCheck.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 end
    // getTodaySafetyOrder
    builder.addCase(Thunks.getTodaySafetyOrder.pending, (state) => {
      state.TodaySafetyOrder.loading = true;
      state.TodaySafetyOrder.data = null;
    });
    builder.addCase(Thunks.getTodaySafetyOrder.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodaySafetyOrder.data = payload;
    });
    builder.addCase(Thunks.getTodaySafetyOrder.rejected, (state) => {
      state.TodaySafetyOrder.loading = false;
      state.TodaySafetyOrder.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 start
    // getTodaySafetyOrderGroup
    builder.addCase(Thunks.getTodaySafetyOrderGroup.pending, (state) => {
      state.TodaySafetyOrder.loading = true;
      state.TodaySafetyOrder.data = null;
    });
    builder.addCase(Thunks.getTodaySafetyOrderGroup.fulfilled, (state, { payload }) => {
      state.SiteUserStatus.loading = false;
      state.TodaySafetyOrder.data = payload;
    });
    builder.addCase(Thunks.getTodaySafetyOrderGroup.rejected, (state) => {
      state.TodaySafetyOrder.loading = false;
      state.TodaySafetyOrder.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 end
    // getPerformance
    builder.addCase(Thunks.getPerformance.pending, (state) => {
      state.Performance.loading = true;
      state.Performance.data = null;
    });
    builder.addCase(Thunks.getPerformance.fulfilled, (state, { payload }) => {
      state.Performance.loading = false;
      state.Performance.data = payload;
    });
    builder.addCase(Thunks.getPerformance.rejected, (state) => {
      state.Performance.loading = false;
      state.Performance.data = null;
    });
    // 2025/02/27 FJ 3月分反映分 NO32 start
    // getPerformanceGroup
    builder.addCase(Thunks.getPerformanceGroup.pending, (state) => {
      state.Performance.loading = true;
      state.Performance.data = null;
    });
    builder.addCase(Thunks.getPerformanceGroup.fulfilled, (state, { payload }) => {
      state.Performance.loading = false;
      state.Performance.data = payload;
    });
    builder.addCase(Thunks.getPerformanceGroup.rejected, (state) => {
      state.Performance.loading = false;
      state.Performance.data = null;
    });
    // 2025/02/27 FJ 3月分反映分 NO32 end
    // getPerformanceWorkArea
    builder.addCase(Thunks.getPerformanceWorkArea.pending, (state) => {
      state.PerformanceWorkArea.loading = true;
      state.PerformanceWorkArea.data = null;
    });
    builder.addCase(Thunks.getPerformanceWorkArea.fulfilled, (state, { payload }) => {
      state.PerformanceWorkArea.loading = false;
      state.PerformanceWorkArea.data = !!state.PerformanceWorkArea.data?.length
        ? [...state.PerformanceWorkArea.data, payload]
        : [payload];
    });
    builder.addCase(Thunks.getPerformanceWorkArea.rejected, (state) => {
      state.PerformanceWorkArea.loading = false;
      state.PerformanceWorkArea.data = null;
    });
    // getKy
    builder.addCase(Thunks.getKy.pending, (state) => {
      state.Ky.loading = true;
      state.Ky.data = null;
    });
    builder.addCase(Thunks.getKy.fulfilled, (state, { payload }) => {
      state.Ky.loading = false;
      state.Ky.data = payload;
    });
    builder.addCase(Thunks.getKy.rejected, (state) => {
      state.Ky.loading = false;
      state.Ky.data = null;
    });

    // 2025/02/25 FJ 3月分反映分 NO32 start
    // getKyGroup
    builder.addCase(Thunks.getKyGroup.pending, (state) => {
      state.Ky.loading = true;
      state.Ky.data = null;
    });
    builder.addCase(Thunks.getKyGroup.fulfilled, (state, { payload }) => {
      state.Ky.loading = false;
      state.Ky.data = payload;
    });
    builder.addCase(Thunks.getKyGroup.rejected, (state) => {
      state.Ky.loading = false;
      state.Ky.data = null;
    });
    // 2025/02/25 FJ 3月分反映分 NO32 end

    //shop schedules
    builder.addCase(Thunks.getShopSchedules.pending, (state) => {
      state.shop.loading = true;
    });
    builder.addCase(Thunks.getShopSchedules.fulfilled, (state, { payload }) => {
      state.shop.loading = false;
      state.shop.data = payload.sites;
    });
    builder.addCase(Thunks.getShopSchedules.rejected, (state) => {
      state.shop.data = initialState.shop.data;
      state.shop.loading = false;
    });

    //schedules date
    builder.addCase(Thunks.getUserStatusChart.pending, (state) => {
      state.schedules.loading = true;
      state.schedules.data = [];
    });
    builder.addCase(Thunks.getUserStatusChart.fulfilled, (state, { payload }) => {
      state.schedules.loading = false;
      state.schedules.data = payload.schedules;
    });
    builder.addCase(Thunks.getUserStatusChart.rejected, (state) => {
      state.schedules.data = initialState.schedules.data;
      state.schedules.loading = false;
    });
  },
});

export const {
  toggleCheckImg,
  removeImg,
  updateManyImg,
  addImage,
  setMaxPoint,
  removeApproved,
  setLocationCmAgent,
  updateStatusSkyCheck,
  setSiteUserStatus,
} = siteDetailSlice.actions;

export default siteDetailSlice.reducer;
